import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useRoosterStore } from '../store/useRoosterStore.js';
import { haalMijnRooster, haalMijnDiensten } from '../lib/api.js';
import { weekNaarMaandag, DAG_LANG } from '../data/afwezigheid.js';
import { isoWeekNummer } from '../data/maand.js';
import { afwezigStijl } from '../lib/roostercodes.js';
import { vandaagNL, parseDag } from '../lib/tijd.js';

const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

// Korte NL-datum ("10 sep") voor een ISO-week + dagcode — zelfde idioom als TeamRooster.
function datumKort(weeknummer, isoJaar, dag) {
  const maandag = weekNaarMaandag(weeknummer, isoJaar);
  const d = new Date(maandag);
  d.setDate(maandag.getDate() + DAGEN.indexOf(dag));
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

// Dienstlabels identiek aan de vorige weergave (incl. tijdvak); valt terug op het server-label.
const DIENST_LABELS = {
  dagdienst: 'Dagdienst (09:00–17:00)',
  late_dienst: 'Late dienst (14:00–22:00)',
  late_dienst_dw: 'Late dienst (14:00–22:00)',
  late_dienst_we: 'Late dienst (14:00–22:00)',
  flexibel: 'Flexibele dienst (~8u)',
};

// Sobere, gecentreerde melding in de bestaande kaart-stijl.
function Melding({ emoji, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
      <div className="text-4xl mb-3">{emoji}</div>
      {children}
    </div>
  );
}

export default function MijnRooster() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { maandSelectie, bekekenMaand, zetBekekenMaand } = useRoosterStore();

  const account = instance.getActiveAccount() ?? accounts[0];
  const email = account?.username ?? null; // MSAL: username = het e-mailadres

  // Lokale maand-state (peiler): geïnitialiseerd uit bekekenMaand (overleeft navigatie),
  // anders maandSelectie, anders de huidige kalendermaand. De vorige/volgende-knoppen
  // wijzigen 'm én schrijven naar de store; het fetch-effect volgt jaar/maand.
  const [peiler, setPeiler] = useState(() => {
    if (bekekenMaand) return { jaar: bekekenMaand.jaar, maand: bekekenMaand.maand };
    if (maandSelectie) return { jaar: maandSelectie.jaar, maand: maandSelectie.maand };
    const nu = new Date();
    return { jaar: nu.getFullYear(), maand: nu.getMonth() + 1 };
  });
  const { jaar, maand } = peiler;

  // Bij een maand-wissel ook de store bijwerken (zetBekekenMaand) zodat de keuze navigatie
  // overleeft. Vanuit de knop-handler, niet uit een effect (react-hooks/set-state-in-effect).
  const vorigeMaand = () => {
    const nieuw = { jaar: maand === 1 ? jaar - 1 : jaar, maand: maand === 1 ? 12 : maand - 1 };
    setPeiler(nieuw);
    zetBekekenMaand(nieuw.jaar, nieuw.maand);
  };
  const volgendeMaand = () => {
    const nieuw = { jaar: maand === 12 ? jaar + 1 : jaar, maand: maand === 12 ? 1 : maand + 1 };
    setPeiler(nieuw);
    zetBekekenMaand(nieuw.jaar, nieuw.maand);
  };

  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(false);
  const [nietGekoppeld, setNietGekoppeld] = useState(false);
  const [fout, setFout] = useState(null);

  // Haal het eigen rooster van de server zodra account of maand wijzigt.
  useEffect(() => {
    if (!isAuthenticated || !email) return;
    let geannuleerd = false;

    const laad = async () => {
      setLaden(true);
      setFout(null);
      setNietGekoppeld(false);
      setData(null);
      try {
        const res = await haalMijnRooster(jaar, maand);
        if (!geannuleerd) setData(res);
      } catch (err) {
        if (geannuleerd) return;
        if (err.status === 400 && /gekoppeld/i.test(err.message)) setNietGekoppeld(true);
        else setFout(err.message);
      } finally {
        if (!geannuleerd) setLaden(false);
      }
    };
    laad();

    return () => { geannuleerd = true; };
  }, [isAuthenticated, email, jaar, maand]);

  // Eerstvolgende werkdag — maand-ONAFHANKELIJK, dus een eigen fetch die alleen aan de login
  // hangt (niet aan de peiler). Puur een extraatje: faalt de call of is er niets, dan tonen
  // we niets (nooit een blokkade). Straatdiensten waarop je afwezig bent vallen af; vaste
  // dagen komen al gefilterd binnen (bouwVasteDagen laat afwezige dagen weg).
  const [volgendeDienst, setVolgendeDienst] = useState(null);
  useEffect(() => {
    if (!isAuthenticated || !email) return;
    let geannuleerd = false;
    haalMijnDiensten()
      .then((res) => {
        if (geannuleerd) return;
        const werkdagen = [
          ...(res.diensten || []).filter((d) => !d.afwezig),
          ...(res.vasteDagen || []),
        ].sort((a, b) => a.datum.localeCompare(b.datum));
        setVolgendeDienst(werkdagen[0] ?? null);
      })
      .catch(() => { if (!geannuleerd) setVolgendeDienst(null); });
    return () => { geannuleerd = true; };
  }, [isAuthenticated, email]);

  // De ISO-week waarin VANDAAG valt — één keer per render, buiten de week-map.
  // vandaagNL() forceert Europe/Amsterdam (nooit de UTC-omweg uit lib/tijd.js: die schuift
  // 's nachts een dag), parseDag() maakt er lokale middernacht van zodat isoWeekNummer op de
  // kalenderdag rekent. Alleen weergave: dit raakt de peiler, de fetches en de store niet.
  const { week: huidigeWeek, isoJaar: huidigIsoJaar } = isoWeekNummer(parseDag(vandaagNL()));

  // Anker + eenmalige scroll naar die week. Staat er een ANDERE maand op het scherm, dan
  // matcht geen enkele week uit data.weken, krijgt geen enkele container de ref, en zet
  // React de ref bij unmount van de vorige maand zelf op null → de optional chaining hieronder
  // doet dan niets. Geen scroll, geen markering, zonder extra conditie.
  // Deps op data.weken: dat is per fetch een nieuwe array, dus dit vuurt per geladen maand
  // één keer — ook als de gebruiker wegnavigeert en terugkomt.
  const huidigeWeekRef = useRef(null);
  useEffect(() => {
    huidigeWeekRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [data?.weken]);

  const maandLabel = `${MAANDEN[maand - 1]} ${jaar}`;
  const naam = data?.medewerker?.naam;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Mijn Rooster</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isAuthenticated ? (naam ? `Rooster van ${naam}` : 'Jouw rooster') : 'Je persoonlijke rooster'}
        </p>
      </div>

      {/* Je volgende werkdag (extraatje) — maand-onafhankelijk, alleen ingelogd + als er een is.
          Een vaste dag heeft alleen soort/label, een straatdienst gemeente + dienst. */}
      {isAuthenticated && volgendeDienst && (
        <div className="bg-brand-50 border border-brand-100 rounded-xl px-4 py-3 text-sm text-brand-800">
          Hierna werk je:{' '}
          <span className="font-semibold">
            {(DAG_LANG[volgendeDienst.dag] || volgendeDienst.dag).toLowerCase()} {datumKort(volgendeDienst.weeknummer, volgendeDienst.isoJaar, volgendeDienst.dag)}
          </span>{' '}
          {volgendeDienst.soort ? (
            <>— {volgendeDienst.label}</>
          ) : (
            <>— {volgendeDienst.gemeenteLabel}, {DIENST_LABELS[volgendeDienst.dienstId] || volgendeDienst.dienstLabel}</>
          )}
        </div>
      )}

      {/* Maand-navigatie (alleen ingelogd) */}
      {isAuthenticated && (
        <div className="flex items-center gap-2">
          <button
            onClick={vorigeMaand}
            aria-label="Vorige maand"
            className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-medium text-slate-700 capitalize min-w-[9rem] text-center">
            {MAANDEN[maand - 1]} {jaar}
          </span>
          <button
            onClick={volgendeMaand}
            aria-label="Volgende maand"
            className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-600"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 1. Niet ingelogd */}
      {!isAuthenticated && (
        <Melding emoji="🔒">
          <p>Log in met je Microsoft-account om je eigen rooster te zien.</p>
          <p className="text-xs mt-2 text-slate-400">
            Gebruik de knop <span className="font-medium">Inloggen met Microsoft</span> rechtsboven.
          </p>
        </Melding>
      )}

      {/* 2. Laden */}
      {isAuthenticated && laden && (
        <Melding emoji="⏳"><p>Rooster laden…</p></Melding>
      )}

      {/* 3. Geen koppeling gevonden */}
      {isAuthenticated && !laden && nietGekoppeld && (
        <Melding emoji="✉️">
          <p>Je account (<span className="font-medium">{email}</span>) is nog niet gekoppeld aan een medewerker.</p>
          <p className="text-xs mt-2 text-slate-400">Vraag de teamleider om je e-mailadres in te vullen bij Beheer.</p>
        </Melding>
      )}

      {/* 4a. Andere serverfout — nette tekst, geen witte pagina */}
      {isAuthenticated && !laden && !nietGekoppeld && fout && (
        <Melding emoji="⚠️">
          <p>Er ging iets mis bij het ophalen van je rooster.</p>
          <p className="text-xs mt-2 text-slate-400">{fout}</p>
        </Melding>
      )}

      {/* 4b. Rooster opgehaald */}
      {isAuthenticated && !laden && !nietGekoppeld && !fout && data && (
        data.gepubliceerd === false ? (
          <Melding emoji="🔒"><p>Het rooster voor {maandLabel} is nog niet gepubliceerd door de teamleider.</p></Melding>
        ) : !Array.isArray(data.weken) || data.weken.length === 0 ? (
          <Melding emoji="📅"><p>Voor {maandLabel} is er geen rooster voor jou.</p></Melding>
        ) : (
          <div className="space-y-4">
            {data.weken.map((week) => {
              const totaal = (week.dagen || []).reduce((s, d) => s + (d.diensten?.length ?? 0) + (d.vast?.length ?? 0), 0);
              // Match op BEIDE velden: rond de jaarwisseling bestaat week 1 in twee ISO-jaren.
              const isHuidigeWeek = week.weeknummer === huidigeWeek && week.isoJaar === huidigIsoJaar;
              return (
                <div
                  key={`${week.isoJaar}-${week.weeknummer}`}
                  ref={isHuidigeWeek ? huidigeWeekRef : null}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
                >
                  <div className="px-5 py-3 border-b border-brand-100 bg-brand-50 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="font-semibold text-brand-800">Week {week.weeknummer}</span>
                      {isHuidigeWeek && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-700">deze week</span>
                      )}
                    </span>
                    <span className="text-xs text-slate-500">{totaal} werkdag{totaal !== 1 ? 'en' : ''}</span>
                  </div>

                  {totaal === 0 ? (
                    <div className="px-5 py-4 text-sm text-slate-400">Geen werkdagen ingepland deze week.</div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {week.dagen.map((dagObj) => (
                        <div key={dagObj.dag} className="px-5 py-3 flex items-start gap-4">
                          <div className="w-20 shrink-0">
                            <div className="text-sm font-semibold text-slate-700">{DAG_LANG[dagObj.dag] || dagObj.dag}</div>
                            <div className="text-xs text-slate-400 mt-0.5">{datumKort(week.weeknummer, week.isoJaar ?? jaar, dagObj.dag)}</div>
                          </div>
                          <div className="flex-1 space-y-1">
                            {dagObj.diensten.map((d, i) => {
                              const mijnType = d.afwezigen?.[naam];
                              // Kleur uit de gedeelde helper; de streep volgt currentColor.
                              const afw = mijnType ? afwezigStijl(mijnType) : null;
                              return (
                                <div key={`straat-${i}`} className="flex items-center gap-2 text-sm">
                                  <span
                                    className={`inline-block w-2 h-2 rounded-full shrink-0 ${afw ? '' : 'bg-blue-500'}`}
                                    style={afw ? { background: afw.kleur } : undefined}
                                  />
                                  <span className={`font-medium ${afw ? 'line-through' : 'text-slate-800'}`} style={afw ? { color: afw.kleur } : undefined}>
                                    {d.gemeente}
                                  </span>
                                  <span className={afw ? 'line-through' : 'text-slate-500'} style={afw ? { color: afw.kleur } : undefined}>
                                    — {DIENST_LABELS[d.dienstId] || d.dienst}
                                  </span>
                                  {afw && <span style={{ color: afw.kleur }}>· {afw.label}</span>}
                                </div>
                              );
                            })}
                            {dagObj.vast.map((v, i) => (
                              <div key={`vast-${i}`} className="flex items-center gap-2 text-sm">
                                <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${v.soort === 'school' ? 'bg-purple-500' : 'bg-slate-400'}`} />
                                <span className="text-slate-800 font-medium">{v.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
